import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'fs_cookie_consent';

export const GDPRBanner = () => {
  const [visible, setVisible]     = useState(false);
  const [expanded, setExpanded]   = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setVisible(false);
  };

  const essentialOnly = () => {
    localStorage.setItem(STORAGE_KEY, 'essential');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-white shadow-[0_-4px_24px_rgba(0,0,0,0.06)]"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="gdpr-banner"
    >
      {/* Gold top accent */}
      <div className="h-[2px] bg-[hsl(var(--primary)/0.5)]" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-20 py-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5">

          {/* Icon + text */}
          <div className="flex items-start gap-4 flex-1">
            <div className="w-9 h-9 border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
              <Cookie className="w-4 h-4 text-[hsl(var(--primary))]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">
                We value your privacy
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                FashionSpyder uses cookies to keep you signed in, remember your preferences,
                and improve platform performance. We do not sell your data.{' '}
                <Link to="/cookies" className="underline underline-offset-2 hover:text-foreground transition-colors">
                  Cookie Policy
                </Link>
                {' '}·{' '}
                <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground transition-colors">
                  Privacy Policy
                </Link>
              </p>

              {/* Expandable details */}
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-2 transition-colors"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? 'Hide details' : 'What cookies do we use?'}
              </button>

              {expanded && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div className="border border-green-200 bg-green-50 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <ShieldCheck className="w-3 h-3 text-green-600" />
                      <span className="font-semibold text-green-800">Essential</span>
                      <span className="text-green-600 ml-auto">Always on</span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Authentication sessions, security tokens, CSRF protection.
                      Required for the platform to function.
                    </p>
                  </div>
                  <div className="border border-border bg-secondary/50 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="font-semibold text-foreground">Functional</span>
                      <span className="text-muted-foreground ml-auto text-xs">Optional</span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Remember your filter preferences, recently viewed profiles,
                      and UI settings between sessions.
                    </p>
                  </div>
                  <div className="border border-border bg-secondary/50 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="font-semibold text-foreground">Analytics</span>
                      <span className="text-muted-foreground ml-auto text-xs">Optional</span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Anonymous usage analytics (PostHog) to help us understand
                      how to improve the platform. No personal data sold.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-row lg:flex-col gap-2.5 flex-shrink-0">
            <Button
              onClick={accept}
              className="bg-foreground hover:bg-foreground/90 text-background text-sm h-9 px-5"
              data-testid="gdpr-accept-btn"
            >
              Accept All
            </Button>
            <Button
              variant="outline"
              onClick={essentialOnly}
              className="border-border hover:border-foreground text-sm h-9 px-5 text-muted-foreground hover:text-foreground"
              data-testid="gdpr-essential-btn"
            >
              Essential Only
            </Button>
          </div>

          {/* Close (same as essential only) */}
          <button
            onClick={essentialOnly}
            className="absolute top-4 right-4 lg:relative lg:top-auto lg:right-auto text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
